import tokens from '@kluein/design-tokens';
import { Box, Button, CheckBox, Heading, Modal, Text } from '@kluein/klue-ui';
import { EnvelopeSimple, Warning, X } from '@phosphor-icons/react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Scroller } from 'components/layout/scroller';
import TRACKING_IDS from 'tracking-ids';

import {
  detectStrippedElements,
  STRIP_CATEGORY,
  type StripCategory,
} from './detect-stripped-elements';

import type { IntelDigestItemType } from 'api/endpoints/intel-digest/intel-digest.types';
import type { Dispatch, RootState } from 'store/store.types';

// Result the parent's onSubmit resolves with — the picker fans out per-digest
// mutations, aggregates counts, and hands the tally back so the parent can
// surface a toast in the vocabulary that matches its content source (card,
// Ask Klue answer, future sources).
export type DigestPickerSubmitResult = {
  totalAdded: number;
  totalAlreadyAdded: number;
  anyFailed: boolean;
};

export type DigestPickerModalProps = {
  // Modal header text — the one thing that differs across content sources.
  title: string;
  // Optional HTML the curator is about to add. When provided, the modal scans
  // for tags/attributes that the digest mailer's `digest_sanitize` strips and
  // shows an itemized confirmation before submitting. Omit for content that
  // is already guaranteed sanitizer-safe (or when the check isn't warranted).
  contentHtml?: string;
  // Adapter-owned submission logic. Called once with all checked digest ids;
  // implementation is free to fan out per-digest mutations or batch them.
  onSubmit: (digestIds: number[]) => Promise<DigestPickerSubmitResult>;
  // Runs after onSubmit resolves. Adapter fires its own toast + closes on
  // whatever outcome it considers successful. Not called if onSubmit throws.
  onSuccess: (result: DigestPickerSubmitResult) => void;
  onClose: () => void;
};

// i18n key per category — copy lives under Common:digestPicker.stripWarning.categories.
const STRIP_CATEGORY_I18N: Record<StripCategory, string> = {
  [STRIP_CATEGORY.iframe]: 'iframe',
  [STRIP_CATEGORY.table]: 'table',
  [STRIP_CATEGORY.video]: 'video',
  [STRIP_CATEGORY.audio]: 'audio',
  [STRIP_CATEGORY.blockquote]: 'blockquote',
  [STRIP_CATEGORY.figure]: 'figure',
  [STRIP_CATEGORY.dynamicBlock]: 'dynamicBlock',
};

// Presentational modal wrapping the digest picker (checkbox list of unsent
// digests, sorted like the Compete 1.0 "Sending Next" overview). Sources its
// list from the Rematch `state.intelDigest.allDigests` slice — the same one
// Alerts's MultipleDigestModal uses. Warms the list itself via
// `intelDigest.fetchAllDigests` if empty on mount.
//
// Content-source specifics (which mutation, what payload, what toast copy)
// live in the adapter that mounts this modal — see AddToDigestModal (cards)
// and AddAnswerToDigestModal (Ask Klue answers).
const DigestPickerModal = ({
  title,
  contentHtml,
  onSubmit,
  onSuccess,
  onClose,
}: DigestPickerModalProps): ReactNode => {
  const { t } = useTranslation(['Card', 'Common']);
  const dispatch = useDispatch<Dispatch>();

  const allDigests = useSelector<RootState, IntelDigestItemType[]>(
    (state) => state.intelDigest.allDigests,
  );
  // Seed the flag from the current store state so the loading indicator
  // renders on the first paint when we know a fetch will fire. Without this,
  // the modal briefly shows the "no digests available" empty state between
  // the initial render and the effect below scheduling the fetch.
  const [isFetchingFallback, setIsFetchingFallback] = useState(
    allDigests.length === 0,
  );
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Non-null when the curator hit Add-to-Digest on content whose HTML contains
  // sanitizer-stripped elements — freezes the selection while they confirm.
  const [pendingConfirm, setPendingConfirm] = useState<StripCategory[] | null>(
    null,
  );

  useEffect(() => {
    if (allDigests.length > 0) return;
    // `fetchAllDigests()` currently caps at 20 draft digests (see
    // `src/store/models/intel-digest/intelDigest.model.ts`). Same limit
    // Alerts's picker inherits; customers with more than 20 drafts see a
    // truncated list here too. Broadening it is a store-level change.
    dispatch.intelDigest
      .fetchAllDigests()
      .finally(() => setIsFetchingFallback(false));
    // Fire once on mount; subsequent state updates flow through useSelector.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Match the ordering the Klue Compete 1.0 digest overview uses in
  // `contexts/_digests.js` — willSendAt ascending, nulls last, ties broken
  // alphabetically by digestType name. Curators pick from the same order they
  // see in "Sending Next".
  const digestItems = useMemo(() => {
    const sorted = [...allDigests].sort(
      (a: IntelDigestItemType, b: IntelDigestItemType) => {
        const aSend = a.willSendAt;
        const bSend = b.willSendAt;
        const aName = a.digestType?.name ?? '';
        const bName = b.digestType?.name ?? '';

        if (aSend && bSend) {
          const cmp = aSend.localeCompare(bSend);
          return cmp === 0 ? aName.localeCompare(bName) : cmp;
        }
        if (aSend) return -1;
        if (bSend) return 1;
        return aName.localeCompare(bName);
      },
    );

    return sorted.map((digest) => ({
      id: digest.id,
      label: digest.digestType?.name ?? digest.title ?? `Digest #${digest.id}`,
    }));
  }, [allDigests]);

  const toggleChecked = (id: number, checked: boolean): void => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const canSubmit = checkedIds.size > 0 && !isSubmitting;

  const submit = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const result = await onSubmit(Array.from(checkedIds));
      onSuccess(result);
    } finally {
      setIsSubmitting(false);
      setPendingConfirm(null);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const categories = detectStrippedElements(contentHtml);
    if (categories.length > 0) {
      setPendingConfirm(categories);
      return;
    }
    await submit();
  };

  const hasDigests = digestItems.length > 0;
  const isLoading = isFetchingFallback && !hasDigests;
  const isConfirming = pendingConfirm !== null;

  return (
    <Modal
      width="xlarge"
      onClose={onClose}
      background={tokens.color.neutral.white.main}
      responsive={false}
      showCloseButton={false}
    >
      <Modal.Header background={tokens.color.primary.main}>
        <Box direction="row" gap="small" align="center">
          <EnvelopeSimple
            size={tokens.iconSize.medium}
            color={tokens.color.primary.contrastmain}
          />
          <Heading
            size="medium"
            margin="none"
            level="2"
            color={tokens.color.primary.contrastmain}
          >
            {title}
          </Heading>
        </Box>
        <Button
          variant="plain"
          onClick={onClose}
          icon={<X color={tokens.color.primary.contrastmain} />}
          data-tracking-id={TRACKING_IDS.global.card.addToDigest.cancel}
        />
      </Modal.Header>
      <Modal.Content>
        {isConfirming && pendingConfirm ? (
          <>
            <Box
              direction="row"
              gap="small"
              align="start"
              margin={{ vertical: 'small' }}
            >
              <Warning
                size={tokens.iconSize.medium}
                color={tokens.color.status.warning[600]}
              />
              <Box gap="xsmall">
                <Text size="large" fontWeight="medium">
                  {t('Common:digestPicker.stripWarning.heading')}
                </Text>
                <Text size="medium">
                  {t('Common:digestPicker.stripWarning.body')}
                </Text>
              </Box>
            </Box>
            <Box as="ul" margin={{ vertical: 'small', left: 'medium' }}>
              {pendingConfirm.map((c) => (
                <Text as="li" key={c} size="medium">
                  {t(
                    `Common:digestPicker.stripWarning.categories.${STRIP_CATEGORY_I18N[c]}`,
                  )}
                </Text>
              ))}
            </Box>
            <Box
              as="footer"
              gap="small"
              margin={{ bottom: 'medium', top: 'large' }}
              direction="row"
              justify="end"
            >
              <Button
                label={t('Common:actions.cancel')}
                onClick={onClose}
                variant="flat"
                size="large"
                data-tracking-id={TRACKING_IDS.global.card.addToDigest.cancel}
              />
              <Button
                label={t('Common:digestPicker.stripWarning.confirmButton')}
                onClick={submit}
                variant="highlight-action"
                size="large"
                data-tracking-id={TRACKING_IDS.global.card.addToDigest.confirm}
                loading={isSubmitting}
              />
            </Box>
          </>
        ) : (
          <>
            {isLoading && (
              <Text size="medium" margin={{ vertical: 'small' }}>
                {t('Common:status.loading')}
              </Text>
            )}
            {!isLoading && !hasDigests && (
              <Text size="medium" margin={{ vertical: 'small' }}>
                {t('Card:actions.addToDigest.noDigests')}
              </Text>
            )}
            {hasDigests && (
              <Scroller pad={{ vertical: 'small' }} height={{ max: 'medium' }}>
                {digestItems.map((d) => (
                  <CheckBox
                    gap="medium"
                    pad={{ bottom: 'small' }}
                    key={d.id}
                    name={String(d.id)}
                    checked={checkedIds.has(d.id)}
                    onChange={(e) => {
                      e.preventDefault();
                      toggleChecked(d.id, e.target.checked);
                    }}
                    label={<Text fontWeight="medium">{d.label}</Text>}
                    data-test-id={`add-to-digest-checkbox-${d.id}`}
                  />
                ))}
              </Scroller>
            )}
            <Box
              as="footer"
              gap="small"
              margin={{ bottom: 'medium', top: 'large' }}
              direction="row"
              justify="end"
            >
              <Button
                label={t('Common:actions.cancel')}
                onClick={onClose}
                variant="flat"
                size="large"
                data-tracking-id={TRACKING_IDS.global.card.addToDigest.cancel}
              />
              <Button
                label={t('Card:actions.addToDigest.confirmButton')}
                onClick={handleSubmit}
                disabled={!canSubmit}
                variant="highlight-action"
                size="large"
                loading={isSubmitting}
                data-tracking-id={TRACKING_IDS.global.card.addToDigest.confirm}
              />
            </Box>
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};

export default DigestPickerModal;
